<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div class="mb-6 flex items-center justify-between">
      <h1 class="text-3xl font-bold text-gray-900 leading-7">
        WhatsApp Templates
      </h1>

      <div class="flex items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          @click="syncTemplates"
          :disabled="syncing || loading"
        >
          <svg
            class="w-4 h-4"
            :class="syncing ? 'animate-spin' : ''"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ syncing ? "Syncing..." : "Sync from Meta" }}
        </button>

        <button
          type="button"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-white text-sm shadow-sm hover:opacity-95"
          style="background-color: #5b9ac5"
          @click="openAdd"
          :disabled="loading"
        >
          <span class="text-base leading-none">＋</span>
          New Template
        </button>
      </div>
    </div>

    <div class="w-64 mb-4">
      <input
        v-model="topSearch"
        type="text"
        placeholder="Search templates"
        class="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-0"
      />
    </div>

    <DynamicTable
      wrapperClass="max-w-full overflow-x-auto"
      :rows="filteredRows"
      :columns="columns"
      :showHeader="false"
      :rowsPerPage="50"
      :rowsPerPageOptions="[10, 20, 50]"
    >
      <template #header_type="{ row }">
        <span v-if="row.header_type === 'IMAGE'" class="text-xs text-gray-600"
          >Image</span
        >
        <span v-else class="text-xs text-gray-300">-</span>
      </template>

      <template #status="{ row }">
        <span
          class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
          :class="statusClass(row.status)"
        >
          {{ row.status }}
        </span>
      </template>

      <template #body_text="{ row }">
        <span
          class="text-xs text-gray-600 line-clamp-2 max-w-xs inline-block align-middle"
        >
          {{ row.body_text }}
        </span>
      </template>

      <template #action="{ row }">
        <div class="flex items-center justify-center gap-4 whitespace-nowrap">
          <button
            class="inline-flex items-center gap-1.5 text-xs text-black hover:opacity-90"
            title="View"
            @click="openView(row)"
          >
            <svg
              viewBox="0 0 24 24"
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <path
                d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
              />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>View</span>
          </button>

          <button
            class="inline-flex items-center gap-1 text-black hover:text-red-700"
            @click="deleteTemplate(row)"
          >
            <i class="fas fa-trash-alt text-red-600"></i>
            <span>Delete</span>
          </button>
        </div>
      </template>
    </DynamicTable>

    <!-- Add / View Modal -->
    <Teleport to="body">
      <div
        v-if="isModalOpen"
        class="fixed inset-0 flex items-end justify-center z-[9999] px-4 pb-10 overflow-y-auto"
        style="background: rgba(15, 23, 42, 0.55); backdrop-filter: blur(6px)"
      >
        <div
          class="bg-white rounded-[18px] shadow-lg relative"
          style="width: 640px; max-width: 92vw"
        >
          <button
            type="button"
            class="absolute right-5 top-4 text-gray-600 hover:text-gray-900 text-lg"
            @click="closeModal"
          >
            ✕
          </button>

          <div class="px-6 pt-6 pb-6">
            <h3 class="text-center text-[18px] font-bold text-gray-900 mb-6">
              {{ modalMode === "view" ? "Template Details" : "New Template" }}
            </h3>

            <!-- VIEW MODE -->
            <div v-if="modalMode === 'view'" class="space-y-4 text-sm">
              <div>
                <span class="font-semibold text-gray-700">Name:</span>
                {{ viewingTemplate?.name }}
              </div>
              <div>
                <span class="font-semibold text-gray-700">Category:</span>
                {{ viewingTemplate?.category }}
              </div>
              <div>
                <span class="font-semibold text-gray-700">Language:</span>
                {{ viewingTemplate?.language }}
              </div>
              <div>
                <span class="font-semibold text-gray-700">Status:</span>
                {{ viewingTemplate?.status }}
              </div>
              <div v-if="viewingTemplate?.header_type === 'IMAGE'">
                <span class="font-semibold text-gray-700">Header:</span> Image
              </div>
              <div>
                <span class="font-semibold text-gray-700">Body:</span>
                <p class="mt-1 p-3 bg-gray-50 rounded-lg whitespace-pre-wrap">
                  {{ viewingTemplate?.body_text }}
                </p>
              </div>
              <div v-if="viewingTemplate?.variables?.length">
                <span class="font-semibold text-gray-700"
                  >Variables (in order):</span
                >
                <ol class="list-decimal list-inside mt-1 text-gray-600">
                  <li v-for="(v, i) in viewingTemplate.variables" :key="i">
                    {{ v }}
                  </li>
                </ol>
              </div>
            </div>

            <!-- CREATE MODE -->
            <form v-else class="space-y-4" @submit.prevent="submitTemplate">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2"
                    >Template Name</label
                  >
                  <input
                    v-model.trim="form.name"
                    type="text"
                    placeholder="booking_confirmation"
                    class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                  />
                  <p class="text-[11px] text-gray-400 mt-1">
                    lowercase_snake_case only
                  </p>
                  <p
                    v-if="fieldError('name')"
                    class="text-xs text-red-600 mt-1"
                  >
                    {{ fieldError("name") }}
                  </p>
                </div>

                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2"
                    >Category</label
                  >
                  <select
                    v-model="form.category"
                    class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                  >
                    <option value="UTILITY">
                      Utility (order/booking updates)
                    </option>
                    <option value="MARKETING">Marketing (promos)</option>
                    <option value="AUTHENTICATION">Authentication (OTP)</option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2"
                    >Language</label
                  >
                  <select
                    v-model="form.language"
                    class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                  >
                    <option value="en_US">English (US)</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>

                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2"
                    >Header</label
                  >
                  <select
                    v-model="form.headerType"
                    class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                    @change="onHeaderTypeChange"
                  >
                    <option value="">None</option>
                    <option value="IMAGE">Image</option>
                  </select>
                </div>
              </div>

              <div v-if="form.headerType === 'IMAGE'">
                <label class="block text-xs font-semibold text-gray-700 mb-2">
                  Example header image
                  <span class="font-normal text-gray-400"
                    >- required by Meta for review, up to 5MB</span
                  >
                </label>

                <div
                  v-if="headerImagePreview"
                  class="relative mt-1 rounded-xl border border-gray-300 overflow-hidden"
                >
                  <img
                    :src="headerImagePreview"
                    class="w-full h-36 object-contain bg-white"
                  />
                  <button
                    type="button"
                    class="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center"
                    @click="removeHeaderImage"
                  >
                    ✕
                  </button>
                </div>

                <div
                  v-else
                  class="mt-1 rounded-xl border border-dashed border-gray-300 p-4 text-center cursor-pointer hover:bg-gray-50"
                  @click="$refs.headerFileInput.click()"
                >
                  <div class="text-sm font-medium text-gray-800">
                    Click to upload
                  </div>
                  <div class="text-xs text-gray-400 mt-1">PNG or JPG</div>
                </div>

                <input
                  ref="headerFileInput"
                  type="file"
                  accept="image/png,image/jpeg"
                  class="hidden"
                  @change="onHeaderFileChange"
                />

                <p
                  v-if="fieldError('header_image')"
                  class="text-xs text-red-600 mt-1"
                >
                  {{ fieldError("header_image") }}
                </p>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-2">
                  Message Body
                  <span class="font-normal text-gray-400">
                    - wrap dynamic values in double curly braces, e.g.
                    &#123;&#123;booking_id&#125;&#125;
                  </span>
                </label>
                <textarea
                  v-model="form.bodyRaw"
                  rows="4"
                  placeholder="Hi {{customer_name}}, your booking {{booking_id}} is confirmed for {{pickup_date}}."
                  class="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none resize-none"
                ></textarea>
                <p
                  v-if="fieldError('bodyRaw')"
                  class="text-xs text-red-600 mt-1"
                >
                  {{ fieldError("bodyRaw") }}
                </p>
              </div>

              <div
                v-if="detectedVariables.length"
                class="p-3 bg-gray-50 rounded-xl"
              >
                <p class="text-xs font-semibold text-gray-700 mb-2">
                  Detected variables (in order - this is what Meta will require
                  sample values for):
                </p>
                <ol class="list-decimal list-inside text-xs text-gray-600 mb-3">
                  <li v-for="(v, i) in detectedVariables" :key="i">{{ v }}</li>
                </ol>

                <p class="text-xs font-semibold text-gray-700 mb-1">Preview:</p>
                <p
                  class="text-xs text-gray-800 bg-white border border-gray-200 rounded-lg p-2 whitespace-pre-wrap"
                >
                  {{ previewText }}
                </p>
              </div>

              <p v-if="generalError" class="text-sm text-red-600">
                {{ generalError }}
              </p>

              <button
                type="submit"
                class="w-full h-11 rounded-full font-bold text-white text-sm hover:opacity-95 disabled:opacity-70 flex items-center justify-center gap-2"
                style="background-color: #5b9ac5"
                :disabled="submitting"
              >
                {{ submitting ? "Submitting..." : "Submit for Approval" }}
              </button>
              <p class="text-[11px] text-gray-400 text-center">
                Meta review typically takes minutes to a few hours for
                Utility/Authentication templates.
              </p>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <div
      v-if="loading"
      class="fixed top-0 left-0 w-screen h-screen flex justify-center items-center z-50 pointer-events-none"
    >
      <div class="loader"></div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import DynamicTable from "@/components/DynamicTable.vue";

const api = axios.create({
  baseURL: "https://marketingvps.lyon-jo.com/api",
});

api.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

export default {
  name: "TemplateManagement",
  components: { DynamicTable },

  data() {
    return {
      templates: [],
      topSearch: "",
      loading: false,
      syncing: false,
      submitting: false,

      isModalOpen: false,
      modalMode: "add",
      viewingTemplate: null,

      form: {
        name: "",
        category: "UTILITY",
        language: "en_US",
        bodyRaw: "",
        headerType: "",
      },
      headerImageFile: null,
      headerImagePreview: "",

      formErrors: {},
      generalError: "",

      columns: [
        { key: "name", label: "Name", align: "left", width: "18%" },
        { key: "category", label: "Category", align: "left", width: "13%" },
        { key: "language", label: "Language", align: "left", width: "9%" },
        { key: "header_type", label: "Header", align: "center", width: "9%" },
        { key: "body_text", label: "Body", align: "left", width: "26%" },
        { key: "status", label: "Status", align: "center", width: "10%" },
        { key: "action", label: "Action", align: "center", width: "15%" },
      ],
    };
  },

  computed: {
    filteredRows() {
      const term = this.topSearch.trim().toLowerCase();
      if (!term) return this.templates;
      return this.templates.filter((t) => t.name.toLowerCase().includes(term));
    },

    detectedVariables() {
      const matches = [
        ...(this.form.bodyRaw || "").matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g),
      ];
      const seen = [];
      matches.forEach((m) => {
        if (!seen.includes(m[1])) seen.push(m[1]);
      });
      return seen;
    },

    metaReadyBody() {
      let body = this.form.bodyRaw || "";
      this.detectedVariables.forEach((v, i) => {
        body = body.replaceAll("{{" + v + "}}", "{{" + (i + 1) + "}}");
      });
      return body;
    },

    previewText() {
      let body = this.form.bodyRaw || "";
      this.detectedVariables.forEach((v) => {
        body = body.replaceAll("{{" + v + "}}", "[" + v.toUpperCase() + "]");
      });
      return body;
    },
  },

  methods: {
    fieldError(key) {
      const v = this.formErrors?.[key];
      return Array.isArray(v) ? v[0] || "" : typeof v === "string" ? v : "";
    },

    statusClass(status) {
      if (status === "APPROVED") return "bg-green-100 text-green-700";
      if (status === "REJECTED") return "bg-red-100 text-red-700";
      return "bg-yellow-100 text-yellow-700";
    },

    async fetchTemplates() {
      this.loading = true;
      try {
        const res = await api.get("/whatsapp/templates");
        this.templates = res.data || [];
      } catch (e) {
        this.toastError(
          e?.response?.data?.message || "Failed to load templates.",
        );
      } finally {
        this.loading = false;
      }
    },

    async syncTemplates() {
      this.syncing = true;
      try {
        await api.post("/whatsapp/templates/sync");
        await this.fetchTemplates();
        this.toastSuccess("Synced with Meta");
      } catch (e) {
        this.toastError(e?.response?.data?.message || "Sync failed.");
      } finally {
        this.syncing = false;
      }
    },

    openAdd() {
      this.formErrors = {};
      this.generalError = "";
      this.modalMode = "add";
      this.form = {
        name: "",
        category: "UTILITY",
        language: "en_US",
        bodyRaw: "",
        headerType: "",
      };
      this.removeHeaderImage();
      this.isModalOpen = true;
    },

    openView(row) {
      this.modalMode = "view";
      this.viewingTemplate = row;
      this.isModalOpen = true;
    },

    closeModal() {
      this.isModalOpen = false;
      this.viewingTemplate = null;
    },

    onHeaderTypeChange() {
      if (this.form.headerType !== "IMAGE") {
        this.removeHeaderImage();
      }
    },

    onHeaderFileChange(e) {
      const f = e.target.files?.[0] || null;
      this.headerImageFile = f;
      if (this.headerImagePreview) {
        URL.revokeObjectURL(this.headerImagePreview);
        this.headerImagePreview = "";
      }
      if (f) {
        this.headerImagePreview = URL.createObjectURL(f);
      }
    },

    removeHeaderImage() {
      this.headerImageFile = null;
      if (this.headerImagePreview) {
        URL.revokeObjectURL(this.headerImagePreview);
      }
      this.headerImagePreview = "";
      if (this.$refs.headerFileInput) this.$refs.headerFileInput.value = "";
    },

    async submitTemplate() {
      if (this.submitting) return;
      this.formErrors = {};
      this.generalError = "";

      if (!/^[a-z0-9_]+$/.test(this.form.name)) {
        this.formErrors.name =
          "Use lowercase letters, numbers, and underscores only.";
        return;
      }
      if (!this.form.bodyRaw.trim()) {
        this.formErrors.bodyRaw = "Body text is required.";
        return;
      }
      if (this.form.headerType === "IMAGE" && !this.headerImageFile) {
        this.formErrors.header_image =
          "An example image is required for an image header.";
        return;
      }

      this.submitting = true;
      try {
        let payload;
        let headers = {};

        if (this.form.headerType === "IMAGE") {
          payload = new FormData();
          payload.append("name", this.form.name);
          payload.append("category", this.form.category);
          payload.append("language", this.form.language);
          payload.append("body_text", this.metaReadyBody);
          payload.append("header_type", "IMAGE");
          payload.append("header_image", this.headerImageFile);
          this.detectedVariables.forEach((v, i) => {
            payload.append("variables[" + i + "]", v);
          });
          headers = { "Content-Type": "multipart/form-data" };
        } else {
          payload = {
            name: this.form.name,
            category: this.form.category,
            language: this.form.language,
            body_text: this.metaReadyBody,
            variables: this.detectedVariables,
          };
        }

        await api.post("/whatsapp/templates", payload, { headers });

        this.toastSuccess("Template submitted for approval");
        this.closeModal();
        await this.fetchTemplates();
      } catch (e) {
        const data = e?.response?.data;
        if (e?.response?.status === 422 && data?.errors) {
          this.formErrors = data.errors;
        } else {
          this.generalError = data?.message || "Failed to submit template.";
        }
      } finally {
        this.submitting = false;
      }
    },

    async deleteTemplate(row) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Delete template?",
        text:
          'This removes "' + row.name + '" from Meta. This cannot be undone.',
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#5B9AC5",
      });
      if (!confirm.isConfirmed) return;

      this.loading = true;
      try {
        await api.delete("/whatsapp/templates/" + row.name);
        await this.fetchTemplates();
        this.toastSuccess("Deleted");
      } catch (e) {
        this.toastError(e?.response?.data?.message || "Failed to delete.");
      } finally {
        this.loading = false;
      }
    },

    toastSuccess(title) {
      return Swal.fire({
        icon: "success",
        title,
        toast: true,
        position: "top",
        timer: 1400,
        showConfirmButton: false,
      });
    },
    toastError(message) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        toast: true,
        position: "top",
        timer: 2200,
        showConfirmButton: false,
      });
    },
  },

  created() {
    this.fetchTemplates();
  },
};
</script>

<style scoped>
.loader {
  width: 46px;
  height: 46px;
  border-radius: 999px;
  border: 4px solid rgba(91, 154, 197, 0.3);
  border-top-color: #5b9ac5;
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
